import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Plus, Trash2, Recycle, FlaskConical, Save, X } from "lucide-react";

const STREAM_CATEGORIES = ["plastic", "paper", "metal", "glass", "organic", "hazardous", "e-waste", "liquid", "cooking_oil", "textile", "general", "other"];
const WASTE_TYPES = ["general", "recyclable", "organic", "hazardous"];
const DISPOSAL_METHODS = ["landfill", "recycling", "composting", "incineration", "reuse"];
const UNITS = ["kg", "L"];

const emptyStream = { name: "", category: "plastic", waste_type: "recyclable", unit: "kg", default_treatment_id: "", active: true, sort_order: 100 };
const emptyTreatment = { name: "", description: "", disposal_method: "recycling", active: true, sort_order: 100 };

/**
 * Admin-only master list of waste streams and treatments used across the app.
 * Route: /app/waste-catalog
 */
export default function WasteCatalog() {
  const { user } = useAuth();
  const [tab, setTab] = useState("streams");
  const [streams, setStreams] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [editingStream, setEditingStream] = useState(null);
  const [editingTreatment, setEditingTreatment] = useState(null);

  const load = async () => {
    const [s, t] = await Promise.all([
      api.get("/waste-catalog/streams?active_only=false"),
      api.get("/waste-catalog/treatments?active_only=false"),
    ]);
    setStreams(s.data);
    setTreatments(t.data);
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);
  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  const treatmentsById = Object.fromEntries(treatments.map((t) => [t.id, t]));

  const saveStream = async () => {
    const body = { ...editingStream, default_treatment_id: editingStream.default_treatment_id || null };
    try {
      if (editingStream.id) {
        await api.put(`/waste-catalog/streams/${editingStream.id}`, body);
      } else {
        await api.post("/waste-catalog/streams", body);
      }
      toast.success("Saved");
      setEditingStream(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const saveTreatment = async () => {
    try {
      if (editingTreatment.id) {
        await api.put(`/waste-catalog/treatments/${editingTreatment.id}`, editingTreatment);
      } else {
        await api.post("/waste-catalog/treatments", editingTreatment);
      }
      toast.success("Saved");
      setEditingTreatment(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const removeStream = async (s) => {
    if (!window.confirm(`Deactivate "${s.name}"? Historical entries keep their label.`)) return;
    await api.delete(`/waste-catalog/streams/${s.id}`);
    toast.success("Deactivated"); load();
  };
  const removeTreatment = async (t) => {
    if (!window.confirm(`Deactivate "${t.name}"?`)) return;
    await api.delete(`/waste-catalog/treatments/${t.id}`);
    toast.success("Deactivated"); load();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Master data"
        title="Waste catalog"
        subtitle="One list of waste streams and treatments — used everywhere staff log data."
        actions={
          <div className="flex items-center gap-2">
            {tab === "streams" && (
              <button onClick={() => setEditingStream({ ...emptyStream })} className="btn-primary inline-flex items-center gap-2" data-testid="add-stream-btn">
                <Plus className="w-4 h-4" /> Add stream
              </button>
            )}
            {tab === "treatments" && (
              <button onClick={() => setEditingTreatment({ ...emptyTreatment })} className="btn-primary inline-flex items-center gap-2" data-testid="add-treatment-btn">
                <Plus className="w-4 h-4" /> Add treatment
              </button>
            )}
          </div>
        }
      />
      <div className="px-8 lg:px-12 py-8 space-y-6">
        <div className="flex gap-1 border-b" style={{ borderColor: "var(--border)" }}>
          <button
            onClick={() => setTab("streams")}
            className={`px-4 py-2 text-sm border-b-2 -mb-px inline-flex items-center gap-2 ${tab === "streams" ? "font-semibold" : "text-neutral-500"}`}
            style={{ borderColor: tab === "streams" ? "var(--forest)" : "transparent" }}
            data-testid="tab-streams"
          >
            <Recycle className="w-4 h-4" /> Waste streams · {streams.filter((s) => s.active).length}
          </button>
          <button
            onClick={() => setTab("treatments")}
            className={`px-4 py-2 text-sm border-b-2 -mb-px inline-flex items-center gap-2 ${tab === "treatments" ? "font-semibold" : "text-neutral-500"}`}
            style={{ borderColor: tab === "treatments" ? "var(--forest)" : "transparent" }}
            data-testid="tab-treatments"
          >
            <FlaskConical className="w-4 h-4" /> Treatments · {treatments.filter((t) => t.active).length}
          </button>
        </div>

        {tab === "streams" && (
          <div className="hbt-card overflow-hidden">
            <table className="hbt-table w-full">
              <thead>
                <tr><th>Name</th><th>Category</th><th>Rolls up as</th><th>Unit</th><th>Default treatment</th><th></th></tr>
              </thead>
              <tbody>
                {streams.map((s) => (
                  <tr key={s.id} data-testid={`stream-row-${s.id}`} style={{ opacity: s.active ? 1 : 0.5 }}>
                    <td className="font-medium">{s.name}{!s.active && <span className="text-[10px] text-neutral-400 ml-2">(inactive)</span>}</td>
                    <td className="text-neutral-600 text-sm capitalize">{s.category.replace("_", " ")}</td>
                    <td><span className={`pill text-[10px] ${s.waste_type === "recyclable" ? "forest" : s.waste_type === "hazardous" ? "error" : s.waste_type === "organic" ? "success" : ""}`}>{s.waste_type}</span></td>
                    <td className="font-mono text-xs">{s.unit}</td>
                    <td className="text-neutral-600 text-sm">{treatmentsById[s.default_treatment_id]?.name || "—"}</td>
                    <td className="text-right">
                      <button onClick={() => setEditingStream({ ...s })} className="text-xs text-neutral-500 hover:text-black mr-2" data-testid={`edit-stream-${s.id}`}>Edit</button>
                      {s.active && <button onClick={() => removeStream(s)} className="text-xs text-neutral-500 hover:text-red-700" data-testid={`del-stream-${s.id}`}><Trash2 className="w-4 h-4 inline" /></button>}
                    </td>
                  </tr>
                ))}
                {streams.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-neutral-500">No streams yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "treatments" && (
          <div className="hbt-card overflow-hidden">
            <table className="hbt-table w-full">
              <thead>
                <tr><th>Name</th><th>Description</th><th>Rolls up as</th><th></th></tr>
              </thead>
              <tbody>
                {treatments.map((t) => (
                  <tr key={t.id} data-testid={`treatment-row-${t.id}`} style={{ opacity: t.active ? 1 : 0.5 }}>
                    <td className="font-medium">{t.name}{!t.active && <span className="text-[10px] text-neutral-400 ml-2">(inactive)</span>}</td>
                    <td className="text-neutral-600 text-sm">{t.description || "—"}</td>
                    <td className="capitalize text-neutral-600 text-sm">{t.disposal_method}</td>
                    <td className="text-right">
                      <button onClick={() => setEditingTreatment({ ...t })} className="text-xs text-neutral-500 hover:text-black mr-2" data-testid={`edit-treatment-${t.id}`}>Edit</button>
                      {t.active && <button onClick={() => removeTreatment(t)} className="text-xs text-neutral-500 hover:text-red-700" data-testid={`del-treatment-${t.id}`}><Trash2 className="w-4 h-4 inline" /></button>}
                    </td>
                  </tr>
                ))}
                {treatments.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-neutral-500">No treatments yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingStream && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditingStream(null)}>
          <div className="hbt-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="stream-editor">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl font-semibold">{editingStream.id ? "Edit stream" : "New waste stream"}</div>
              <button onClick={() => setEditingStream(null)}><X className="w-5 h-5 text-neutral-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="hbt-label block mb-2">Name</label>
                <input className="hbt-input" value={editingStream.name} onChange={(e) => setEditingStream({ ...editingStream, name: e.target.value })} placeholder="e.g. Cereal packaging film" data-testid="stream-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="hbt-label block mb-2">Category</label>
                  <select className="hbt-input capitalize" value={editingStream.category} onChange={(e) => setEditingStream({ ...editingStream, category: e.target.value })} data-testid="stream-category-select">
                    {STREAM_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
                  </select>
                </div>
                <div>
                  <label className="hbt-label block mb-2">Unit</label>
                  <select className="hbt-input" value={editingStream.unit} onChange={(e) => setEditingStream({ ...editingStream, unit: e.target.value })} data-testid="stream-unit-select">
                    {UNITS.map((u) => <option key={u} value={u}>{u === "L" ? "L (litres)" : "kg (kilograms)"}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="hbt-label block mb-2">Rolls up as (reports)</label>
                  <select className="hbt-input capitalize" value={editingStream.waste_type} onChange={(e) => setEditingStream({ ...editingStream, waste_type: e.target.value })} data-testid="stream-wtype-select">
                    {WASTE_TYPES.map((w) => <option key={w} value={w}>{w}</option>)}
                  </select>
                </div>
                <div>
                  <label className="hbt-label block mb-2">Sort order</label>
                  <input type="number" className="hbt-input" value={editingStream.sort_order} onChange={(e) => setEditingStream({ ...editingStream, sort_order: Number(e.target.value) })} />
                </div>
              </div>
              <div>
                <label className="hbt-label block mb-2">Default treatment (auto-suggested when picked)</label>
                <select className="hbt-input" value={editingStream.default_treatment_id || ""} onChange={(e) => setEditingStream({ ...editingStream, default_treatment_id: e.target.value })} data-testid="stream-default-treatment">
                  <option value="">— None —</option>
                  {treatments.filter((t) => t.active).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-neutral-600">
                <input type="checkbox" checked={editingStream.active} onChange={(e) => setEditingStream({ ...editingStream, active: e.target.checked })} />
                Active (shown to staff)
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditingStream(null)} className="btn-outline">Cancel</button>
              <button onClick={saveStream} className="btn-primary inline-flex items-center gap-2" data-testid="save-stream-btn"><Save className="w-4 h-4" />Save</button>
            </div>
          </div>
        </div>
      )}

      {editingTreatment && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditingTreatment(null)}>
          <div className="hbt-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="treatment-editor">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl font-semibold">{editingTreatment.id ? "Edit treatment" : "New treatment"}</div>
              <button onClick={() => setEditingTreatment(null)}><X className="w-5 h-5 text-neutral-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="hbt-label block mb-2">Name</label>
                <input className="hbt-input" value={editingTreatment.name} onChange={(e) => setEditingTreatment({ ...editingTreatment, name: e.target.value })} placeholder="e.g. Local composting facility" data-testid="treatment-name-input" />
              </div>
              <div>
                <label className="hbt-label block mb-2">Description (optional)</label>
                <textarea rows={2} className="hbt-input" value={editingTreatment.description || ""} onChange={(e) => setEditingTreatment({ ...editingTreatment, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="hbt-label block mb-2">Rolls up as (reports)</label>
                  <select className="hbt-input capitalize" value={editingTreatment.disposal_method} onChange={(e) => setEditingTreatment({ ...editingTreatment, disposal_method: e.target.value })} data-testid="treatment-disposal-select">
                    {DISPOSAL_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="hbt-label block mb-2">Sort order</label>
                  <input type="number" className="hbt-input" value={editingTreatment.sort_order} onChange={(e) => setEditingTreatment({ ...editingTreatment, sort_order: Number(e.target.value) })} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-neutral-600">
                <input type="checkbox" checked={editingTreatment.active} onChange={(e) => setEditingTreatment({ ...editingTreatment, active: e.target.checked })} />
                Active
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditingTreatment(null)} className="btn-outline">Cancel</button>
              <button onClick={saveTreatment} className="btn-primary inline-flex items-center gap-2" data-testid="save-treatment-btn"><Save className="w-4 h-4" />Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
